import React, {JSX, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import Icons from 'components/atoms/Icons';
import { Field, ImageField, LinkField, RichTextField, Text, RichText, Link, Image, } from '@sitecore-jss/sitecore-jss-nextjs';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { classNames } from 'assets/utils/helper';
import VideoPlayer from 'components/molecules/VideoPlayer';
import { ItemValue } from 'components/types/ListItem';
import { StyleguideComponentProps } from 'lib/component-props';

type CardCarouselChildItem = {
  fields: {
    Intro: Field<string>;
    Body: RichTextField;
    CTA: LinkField;
    Image: ImageField;
    VideoURL: Field<string>;
    BasePhoneNumber: Field<string>;
  };
};

type CardCarouselParentProps = StyleguideComponentProps & {
  fields: {
    Title: Field<string>;
    Headline: Field<string>;
    CardCarouselChildList: CardCarouselChildItem[];
    BackgroundColor: ItemValue;
    MarginSize?: {
      fields?: {
        heading?: {
          value?: string;
        };
      };
    };
  };
};

const MAX_SLIDES = 6;
// Small layout breakpoint (<=768)
const useIsSmallCarousel = (): boolean => {
  const [isSmall, setIsSmall] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Toggle 1 card mode on smaller screens.
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => setIsSmall(mq.matches);

    apply();

    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  return isSmall;
};
// swipe on tablets (<=1023)
const useIsSwipeEnabled = (): boolean => {
  const [isSwipeEnabled, setIsSwipeEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mq = window.matchMedia('(max-width: 1023px)');
    const apply = () => setIsSwipeEnabled(mq.matches);

    apply();

    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  return isSwipeEnabled;
};

type AnimPhase = 'idle' | 'prepare' | 'run';

type CardCarouselCardProps = {
  item: CardCarouselChildItem;
  isPeek?: boolean;
  imageRef?: React.Ref<HTMLDivElement>;
  enableVideoSwipeLayer?: boolean;
  onVideoSwipe?: (direction: 'next' | 'prev') => void;
  isActive?: boolean;
  showMedia?: boolean;
  showIntro?: boolean;
  showBody?: boolean;
  showCTA?: boolean;
};

const CardCarouselCard = ({
  item,
  isPeek,
  imageRef,
  enableVideoSwipeLayer = false,
  onVideoSwipe,
  isActive = true,
  showMedia = true,
  showIntro = true,
  showBody = true,
  showCTA = true,
}: CardCarouselCardProps): JSX.Element => {
  const { fields } = item;

  const hasImage = Boolean(fields.Image?.value?.src);
  const hasVideo = Boolean(fields.VideoURL?.value);
  const phoneNumber = fields.BasePhoneNumber?.value?.trim();
  const ctaText = fields.CTA?.value?.text?.trim();
  const videoTouchStartX = useRef<number | null>(null);
  const videoTouchStartY = useRef<number | null>(null);

  const handleVideoTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!enableVideoSwipeLayer) return;

      e.stopPropagation();

      videoTouchStartX.current = e.touches[0]?.clientX ?? null;
      videoTouchStartY.current = e.touches[0]?.clientY ?? null;
    },
    [enableVideoSwipeLayer]
  );

  const handleVideoTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!enableVideoSwipeLayer) return;

      const startX = videoTouchStartX.current;
      const startY = videoTouchStartY.current;
      const endX = e.changedTouches[0]?.clientX ?? null;
      const endY = e.changedTouches[0]?.clientY ?? null;

      videoTouchStartX.current = null;
      videoTouchStartY.current = null;

      if (startX == null || startY == null || endX == null || endY == null) {
        return;
      }

      const deltaX = startX - endX;
      const deltaY = startY - endY;

      const horizontalThreshold = 50;

      const isHorizontalSwipe =
        Math.abs(deltaX) > horizontalThreshold &&
        Math.abs(deltaX) > Math.abs(deltaY) * 1.2;

      if (!isHorizontalSwipe) return;

      e.preventDefault();
      e.stopPropagation();

      onVideoSwipe?.(deltaX > 0 ? 'next' : 'prev');
    },
    [enableVideoSwipeLayer, onVideoSwipe]
  );

  if (isPeek) {
    // Peek card is image only by design.
    return (
      <article className="card-carousel-card is-peek" aria-hidden="true">
        <div className="card-carousel-card-inner">
          {hasImage && (
            <div className="card-carousel-image" ref={imageRef}>
              <Image field={fields.Image} />
            </div>
          )}
        </div>
      </article>
    );
  }

  return (
    <article className="card-carousel-card">
      <div className="card-carousel-card-inner">
        {showMedia && (
          <div
            className={classNames(
              'card-carousel-image',
              hasVideo ? 'has-video' : ''
            )}
            ref={imageRef}
          >
            {hasVideo && isActive ? (
              <>
                <VideoPlayer
                  VideoUrl={fields.VideoURL}
                  Thumbnail={fields.Image}
                  allowFullScreen
                />

                {enableVideoSwipeLayer && (
                  <div
                    className="card-carousel-video-swipe-zones"
                    aria-hidden="true"
                  >
                    <div
                      className="card-carousel-video-swipe-zone card-carousel-video-swipe-zone--top"
                      onTouchStart={handleVideoTouchStart}
                      onTouchEnd={handleVideoTouchEnd}
                    />
                    <div
                      className="card-carousel-video-swipe-zone card-carousel-video-swipe-zone--left"
                      onTouchStart={handleVideoTouchStart}
                      onTouchEnd={handleVideoTouchEnd}
                    />
                    <div
                      className="card-carousel-video-swipe-zone card-carousel-video-swipe-zone--right"
                      onTouchStart={handleVideoTouchStart}
                      onTouchEnd={handleVideoTouchEnd}
                    />
                  </div>
                )}
              </>
            ) : hasImage ? (
              <Image field={fields.Image} />
            ) : null}
          </div>
        )}

        {(showIntro || showBody) && (
          <div className="card-carousel-copy">
            {showIntro && (
              <div className="card-carousel-intro">
                {fields.Intro?.value && (
                  <Text
                    field={fields.Intro}
                    tag="p"
                    className="osf-display-h3"
                  />
                )}
              </div>
            )}

            {showBody && (
              <div className="card-carousel-body osf-body-copy">
                {fields.Body?.value && <RichText field={fields.Body} />}
              </div>
            )}
          </div>
        )}

        {showCTA && (
          <div className="card-carousel-cta">
            {phoneNumber ? (
              <a
                href={`tel:${phoneNumber}`}
                className="btn-mobile-fw btn-primary"
              >
                {ctaText || phoneNumber}
              </a>
            ) : (
              fields.CTA?.value?.href && (
                <Link
                  field={fields.CTA}
                  className="btn-mobile-fw btn-primary"
                />
              )
            )}
          </div>
        )}
      </div>
    </article>
  );
};

const CardCarouselParent = (props: CardCarouselParentProps): JSX.Element => {
  const { fields } = props;

  const isSmall = useIsSmallCarousel();
  const isSwipeEnabled = useIsSwipeEnabled();
  const cardsPerSlide = isSmall ? 1 : 2;

  const marginSize = getMarginSize(
    fields?.MarginSize?.fields?.heading?.value ?? ''
  );

  const backgroundColorName = fields.BackgroundColor?.name?.toLowerCase();

  const hasBg =
    backgroundColorName === 'bg-primary' ||
    backgroundColorName === 'bg-secondary';

  const cards = useMemo(() => {
    const maxCards = cardsPerSlide * MAX_SLIDES;
    return (fields.CardCarouselChildList ?? []).slice(0, maxCards);
  }, [fields.CardCarouselChildList, cardsPerSlide]);

  const sectionVisibility = useMemo(
    () => ({
      showMedia: cards.some(
        (card) =>
          Boolean(card.fields.Image?.value?.src) ||
          Boolean(card.fields.VideoURL?.value)
      ),
      showIntro: cards.some((card) => Boolean(card.fields.Intro?.value)),
      showBody: cards.some((card) => Boolean(card.fields.Body?.value)),
      showCTA: cards.some(
        (card) =>
          Boolean(card.fields.CTA?.value?.href) ||
          Boolean(card.fields.BasePhoneNumber?.value?.trim())
      ),
    }),
    [cards]
  );

  const slides = useMemo(() => {
    // Chunk cards into slides 1 card or 2 cards depending on breakpoints.
    const result: CardCarouselChildItem[][] = [];
    for (let i = 0; i < cards.length; i += cardsPerSlide) {
      result.push(cards.slice(i, i + cardsPerSlide));
    }
    return result.slice(0, MAX_SLIDES);
  }, [cards, cardsPerSlide]);

  const totalSlides = slides.length;

  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    setActiveSlide((prev) => Math.min(prev, Math.max(0, totalSlides - 1)));
  }, [totalSlides]);

  const canGoPrev = activeSlide > 0;
  const canGoNext = activeSlide < totalSlides - 1;

  const handlePrevSmall = useCallback(() => {
    setActiveSlide((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextSmall = useCallback(() => {
    setActiveSlide((prev) => Math.min(totalSlides - 1, prev + 1));
  }, [totalSlides]);

  const [phase, setPhase] = useState<AnimPhase>('idle');
  const [toSlide, setToSlide] = useState<number | null>(null);
  const dirRef = useRef<'next' | 'prev'>('next');
  const [isResetting, setIsResetting] = useState(false);

  const startAnim = useCallback(
    (dir: 'next' | 'prev') => {
      if (isSmall) {
        if (dir === 'next') {
          handleNextSmall();
        } else {
          handlePrevSmall();
        }

        return;
      }
      if (phase !== 'idle' || isResetting) return;

      const target =
        dir === 'next'
          ? Math.min(totalSlides - 1, activeSlide + 1)
          : Math.max(0, activeSlide - 1);

      if (target === activeSlide) return;

      dirRef.current = dir;
      setToSlide(target);
      setPhase('prepare');
    },
    [
      isSmall,
      phase,
      isResetting,
      totalSlides,
      activeSlide,
      handleNextSmall,
      handlePrevSmall,
    ]
  );

  useEffect(() => {
        // Two step phase change ensures the browser watches initial transforms before animating.
    if (phase !== 'prepare') return;
    const raf = requestAnimationFrame(() => {
      setPhase('run');
    });
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  const finishAnim = useCallback(() => {
    if (toSlide == null) return;
    setIsResetting(true);
    setActiveSlide(toSlide);
    setToSlide(null);

    requestAnimationFrame(() => {
      setPhase('idle');

      requestAnimationFrame(() => {
        setIsResetting(false);
      });
    });
  }, [toSlide]);

  const handleDotClick = useCallback(
    (index: number) => {
      if (!isSmall && (phase !== 'idle' || isResetting)) return;
      setActiveSlide(index);
    },
    [isSmall, phase, isResetting]
  );

  const peekItem = useMemo(() => {
    // Peek shows upcoming card.
    if (!canGoNext) return null;

    if (isSmall) {
      const nextCardIndex = activeSlide + 1;
      return cards[nextCardIndex] ?? null;
    }

    const nextSlideIndex = Math.min(
      activeSlide + 1,
      Math.max(0, totalSlides - 1)
    );
    return slides[nextSlideIndex]?.[0] ?? null;
  }, [canGoNext, isSmall, activeSlide, cards, slides, totalSlides]);

  const touchStartX = useRef<number | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      // Swipe on smaller screens.
      if (!isSwipeEnabled) return;

      const startX = touchStartX.current;
      const endX = e.changedTouches[0]?.clientX ?? null;
      touchStartX.current = null;

      if (startX == null || endX == null) return;

      const delta = startX - endX;
      const threshold = 50;

      if (delta > threshold && canGoNext) {
        if (isSmall) {
          handleNextSmall();
        } else {
          startAnim('next');
        }
      }
      if (delta < -threshold && canGoPrev) {
        if (isSmall) {
          handlePrevSmall();
        } else {
          startAnim('prev');
        }
      }
    },
    [
      isSwipeEnabled,
      canGoNext,
      canGoPrev,
      isSmall,
      handleNextSmall,
      handlePrevSmall,
      startAnim,
    ]
  );

  const handleVideoSwipe = useCallback(
    (direction: 'next' | 'prev') => {
      if (direction === 'next' && canGoNext) {
        if (isSmall) {
          handleNextSmall();
        } else {
          startAnim('next');
        }
      }

      if (direction === 'prev' && canGoPrev) {
        if (isSmall) {
          handlePrevSmall();
        } else {
          startAnim('prev');
        }
      }
    },
    [
      canGoNext,
      canGoPrev,
      isSmall,
      handleNextSmall,
      handlePrevSmall,
      startAnim,
    ]
  );

  const stageClasses = !isSmall
    ? `card-carousel-stage dir-${dirRef.current} ${
        phase === 'run' ? 'is-animating' : ''
      } ${isResetting ? 'is-resetting' : ''}`
    : '';

  const isAnimatingDesktop = !isSmall && (phase !== 'idle' || isResetting);

  const fullbleedRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const imageProbeRef = useRef<HTMLDivElement | null>(null);
  const measurementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!fullbleedRef.current || !containerRef.current || !frameRef.current) {
      return;
    }

    const root = fullbleedRef.current;
    const container = containerRef.current;
    const frame = frameRef.current;

    const update = () => {
            // Measuring real layout so peek and animation stays aligned to the container, not the page.
      const rootRect = root.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const frameRect = frame.getBoundingClientRect();

      const gutterRight = Math.max(0, window.innerWidth - containerRect.right);
      const peekLeft = Math.max(0, containerRect.right - rootRect.left);

      const slide = root.querySelector('.card-carousel-slide.is-double');
      const gap = slide
        ? parseFloat(window.getComputedStyle(slide).columnGap) || 0
        : 0;

      const frameW = frameRect.width;
      const cardW = isSmall ? frameW : Math.max(0, (frameW - gap) / 2);
      const basePeekTop = Math.max(0, frameRect.top - rootRect.top);

      root.style.setProperty(
        '--cc-gutter-right',
        `${Math.round(gutterRight)}px`
      );
      root.style.setProperty('--cc-peek-left', `${Math.round(peekLeft)}px`);
      root.style.setProperty('--cc-peek-top', `${Math.round(basePeekTop)}px`);
      root.style.setProperty('--cc-card-w-px',`${Math.round(cardW)}px`);

      const probe = imageProbeRef.current;
      if (probe) {
        const probeRect = probe.getBoundingClientRect();
        root.style.setProperty(
          '--cc-image-h',
          `${Math.round(probeRect.height)}px`
        );
      }
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(root);
    resizeObserver.observe(container);
    resizeObserver.observe(frame);

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, [activeSlide, isSmall]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = fullbleedRef.current;
    const measurementContainer = measurementRef.current;

    if (!root || !measurementContainer) return;

    let animationFrameId = 0;
    let isMounted = true;

    const sections = [
      {
        selector: '.card-carousel-measurement-intro',
        property: '--cc-intro-min-height',
      },
      {
        selector: '.card-carousel-measurement-body',
        property: '--cc-body-min-height',
      },
      {
        selector: '.card-carousel-measurement-cta',
        property: '--cc-cta-min-height',
      },
    ];

    const syncAllCardSectionHeights = () => {
      cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        if (!isMounted) return;

        sections.forEach(({ selector, property }) => {
          const elements = Array.from(
            measurementContainer.querySelectorAll<HTMLElement>(selector)
          );

          if (!elements.length) {
            root.style.setProperty(property, '0px');
            return;
          }

          const maxHeight = Math.max(
            ...elements.map((element) =>
              Math.ceil(element.getBoundingClientRect().height)
            )
          );

          root.style.setProperty(
            property,
            `${Math.max(0, maxHeight)}px`
          );
        });
      });
    };

    syncAllCardSectionHeights();

    const resizeObserver = new ResizeObserver(() => {
      syncAllCardSectionHeights();
    });

    resizeObserver.observe(measurementContainer);

    const measurementCards = Array.from(
      measurementContainer.querySelectorAll<HTMLElement>(
        '.card-carousel-measurement-card'
      )
    );

    measurementCards.forEach((card) => {
      resizeObserver.observe(card);
    });

    window.addEventListener('resize', syncAllCardSectionHeights);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (isMounted) {
          syncAllCardSectionHeights();
        }
      });
    }

    return () => {
      isMounted = false;

      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();

      window.removeEventListener(
        'resize',
        syncAllCardSectionHeights
      );
    };
  }, [
    cards,
    isSmall,
    sectionVisibility.showIntro,
    sectionVisibility.showBody,
    sectionVisibility.showCTA,
  ]);

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`card-carousel-${props.rendering?.uid}`}
      aria-label="Card Carousel"
    >
      {totalSlides > 0 && (
        <section
          ref={fullbleedRef}
          className={classNames(
            'card-carousel-fullbleed',
            hasBg ? `with-bg ${backgroundColorName}` : '',
            isAnimatingDesktop ? `is-animating dir-${dirRef.current}` : '',
            isResetting ? 'is-resetting' : ''
          )}
          aria-label="Card carousel"
        >
          <div ref={containerRef} className="jss-inner-container">
            <header className="card-carousel-header">
              <Text
                field={fields.Title}
                tag="h2"
                className="card-carousel-title"
              />
              {fields.Headline?.value && (
                <Text
                  field={fields.Headline}
                  tag="p"
                  className="osf-intro-copy"
                />
              )}
            </header>

            <div className="card-carousel-wrap">
              <div
                className={`card-carousel-inner ${isSmall ? 'is-small' : ''}`}
              >
                {!isSmall && totalSlides > 1 ? (
                  <button
                    type="button"
                    className="card-carousel-arrow card-carousel-arrow--prev"
                    onClick={() => startAnim('prev')}
                    disabled={!canGoPrev || phase !== 'idle' || isResetting}
                    aria-label="Previous slide"
                  >
                    <Icons id="chevron-left-icon" className="ce-chevron" />
                  </button>
                ) : !isSmall ? (
                  <span
                    className="card-carousel-arrow-spacer"
                    aria-hidden="true"
                  />
                ) : null}

                <div ref={frameRef} className="card-carousel-frame">
                  <div
                    className="card-carousel-viewport"
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                  >
                    {isSmall ? (
                      <div
                        className="card-carousel-track"
                        style={{
                          transform: `translateX(-${activeSlide * 100}%)`,
                        }}
                        role="group"
                        aria-roledescription="carousel"
                        aria-label={`Slide ${activeSlide + 1} of ${totalSlides}`}
                      >
                        {slides.map((slideCards, slideIndex) => (
                          <div
                            key={`slide-${slideIndex}`}
                            className="card-carousel-slide is-single"
                            aria-hidden={slideIndex !== activeSlide}
                          >
                            {slideCards.map((child, cardIndex) => (
                              <CardCarouselCard
                                key={`card-${slideIndex}-${cardIndex}`}
                                item={child}
                                imageRef={
                                  slideIndex === activeSlide && cardIndex === 0
                                    ? imageProbeRef
                                    : undefined
                                }
                                enableVideoSwipeLayer={
                                  isSwipeEnabled &&
                                  slideIndex === activeSlide
                                }
                                onVideoSwipe={handleVideoSwipe}
                                isActive={slideIndex === activeSlide}
                                showMedia={
                                  sectionVisibility.showMedia
                                }
                                showIntro={
                                  sectionVisibility.showIntro
                                }
                                showBody={sectionVisibility.showBody}
                                showCTA={sectionVisibility.showCTA}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className={stageClasses}
                        role="group"
                        aria-roledescription="carousel"
                        aria-label={`Slide ${activeSlide + 1} of ${totalSlides}`}
                      >
                        <div className="card-carousel-panel is-current">
                          <div className="card-carousel-slide is-double">
                            {slides[activeSlide]?.map(
                              (child, cardIndex) => (
                                <CardCarouselCard
                                  key={`cur-${activeSlide}-${cardIndex}`}
                                  item={child}
                                  imageRef={
                                    cardIndex === 0
                                      ? imageProbeRef
                                      : undefined
                                  }
                                  enableVideoSwipeLayer={
                                    isSwipeEnabled &&
                                    phase === 'idle' &&
                                    !isResetting
                                  }
                                  onVideoSwipe={handleVideoSwipe}
                                  isActive={
                                    phase === 'idle' &&
                                    !isResetting
                                  }
                                  showMedia={
                                    sectionVisibility.showMedia
                                  }
                                  showIntro={
                                    sectionVisibility.showIntro
                                  }
                                  showBody={
                                    sectionVisibility.showBody
                                  }
                                  showCTA={
                                    sectionVisibility.showCTA
                                  }
                                />
                              )
                            )}
                          </div>
                        </div>

                        {phase !== 'idle' && toSlide != null && (
                          <div
                            className={`card-carousel-panel is-incoming ${
                              dirRef.current === 'next'
                                ? 'is-incoming--from-peek'
                                : 'is-incoming--from-left'
                            }`}
                            onTransitionEnd={(e) => {
                              if (e.propertyName !== 'transform') {
                                return;
                              }
                              if (phase !== 'run') {
                                return;
                              }
                              finishAnim();
                            }}
                          >
                            <div className="card-carousel-slide is-double">
                              {slides[toSlide]?.map(
                                (child, cardIndex) => (
                                  <CardCarouselCard
                                    key={`in-${toSlide}-${cardIndex}`}
                                    item={child}
                                    enableVideoSwipeLayer={false}
                                    onVideoSwipe={handleVideoSwipe}
                                    isActive={false}
                                    showMedia={
                                      sectionVisibility.showMedia
                                    }
                                    showIntro={
                                      sectionVisibility.showIntro
                                    }
                                    showBody={
                                      sectionVisibility.showBody
                                    }
                                    showCTA={
                                      sectionVisibility.showCTA
                                    }
                                  />
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!isSmall && totalSlides > 1 ? (
                  <button
                    type="button"
                    className="card-carousel-arrow card-carousel-arrow--next"
                    onClick={() => startAnim('next')}
                    disabled={!canGoNext || phase !== 'idle' || isResetting}
                    aria-label="Next slide"
                  >
                    <Icons id="chevron-left-icon" className="ce-chevron" />
                  </button>
                ) : !isSmall ? (
                  <span
                    className="card-carousel-arrow-spacer"
                    aria-hidden="true"
                  />
                ) : null}
              </div>

              {totalSlides > 1 && (
                <div
                  className="card-carousel-dots"
                  aria-label="Carousel pagination"
                >
                  {Array.from({ length: totalSlides }).map((_, idx) => (
                    <button
                      key={`dot-${idx}`}
                      type="button"
                      className={`card-carousel-dot ${
                        idx === activeSlide ? 'is-active' : ''
                      }`}
                      onClick={() => handleDotClick(idx)}
                      aria-label={`Go to slide ${idx + 1}`}
                      aria-current={idx === activeSlide ? 'true' : undefined}
                      disabled={!isSmall && (phase !== 'idle' || isResetting)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {canGoNext && peekItem ? (
            <div className="card-carousel-peek-outside" aria-hidden="true" >
              {(!isAnimatingDesktop || isSmall) && (
                <div className="card-carousel-peek-inner">
                  <CardCarouselCard item={peekItem} isPeek />
                </div>
              )}
              <div className="card-carousel-peek-fade" />
            </div>
          ) : null}
          <div
            ref={measurementRef}
            className="card-carousel-measurement"
            aria-hidden="true"
          >
            {cards.map((card, index) => (
              <div
                key={`measurement-card-${index}`}
                className="card-carousel-measurement-card"
              >
                <div className="card-carousel-measurement-card-inner">
                  {sectionVisibility.showIntro && (
                    <div className="card-carousel-measurement-intro">
                      {card.fields.Intro?.value && (
                        <Text
                          field={card.fields.Intro}
                          tag="p"
                          className="osf-display-h3"
                        />
                      )}
                    </div>
                  )}

                  {sectionVisibility.showBody && (
                    <div className="card-carousel-measurement-body osf-body-copy">
                      {card.fields.Body?.value && (
                        <RichText field={card.fields.Body} />
                      )}
                    </div>
                  )}

                  {sectionVisibility.showCTA && (
                  <div className="card-carousel-measurement-cta">
                    {card.fields.BasePhoneNumber?.value?.trim() ? (
                      <a
                        href={`tel:${card.fields.BasePhoneNumber.value.trim()}`}
                        className="btn-mobile-fw btn-primary"
                        tabIndex={-1}
                      >
                        {card.fields.CTA?.value?.text?.trim() ||
                          card.fields.BasePhoneNumber.value.trim()}
                      </a>
                    ) : (
                      card.fields.CTA?.value?.href && (
                        <Link
                          field={card.fields.CTA}
                          className="btn-mobile-fw btn-primary"
                          tabIndex={-1}
                        />
                      )
                    )}
                  </div>
                )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default CardCarouselParent;
